import { useFormContext } from 'react-hook-form';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import type { Theme } from '@mui/material';
import { Box, Tooltip, Typography } from '@mui/material';
import { ChipInput, CircularProgress, SuspenseErrorFallback } from 'components/common';
import useRowsAdSets from '../hooks/useRowsAdSets';
import type { GridRowSelectionModel } from '@mui/x-data-grid/models/gridRowSelectionModel';
import { FormContainer } from 'components/layout';
import type {
  GridColumnHeaderParams,
  GridRenderCellParams,
  GridRowProps,
  GridRowsProp,
  RowPropsOverrides,
} from '@mui/x-data-grid';
import { DataGrid, GridRow } from '@mui/x-data-grid';
import type { MarketingProcess } from 'types/shared';
import { isSimplifiedFlowProcess } from '../utils/marketingProcess';
import { DuplicateStatusMenu } from './FacebookFormComponents';
import { AdSetStatusSwitcher, AdSetTag } from './AdSetsFormComponents';
import { MemoizedAdSetBudgetField } from './AdSetBudgetField';
import { MemoizedAdSetDuplicatesCountField } from './AdSetDuplicatesCountField';
import { MemoizedAdSetStartTimeField } from './AdSetStartTimeField';
import { StartTimeDialog } from './StartTimeDialog';
import type { AdSetEntry } from '../api/getAdSets';
import { isEmpty } from 'lodash-es';
import { InfoOutlined } from '@mui/icons-material';
import type { CampaignEntry } from '../api';
import { BudgetsHeader, StartTimeHeader } from './TableHeaders';

const DuplicatesHeader = ({ headerName }: { headerName?: string }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
    <Typography sx={{ fontWeight: 500, fontSize: 14 }}>{headerName}</Typography>
    <Tooltip title="Adset duplicates for each pack. 5 is max">
      <InfoOutlined sx={{ fontSize: 18 }} />
    </Tooltip>
  </Box>
);

const TagHeader = ({ headerName }: { headerName?: string }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
    <Typography sx={{ fontWeight: 500, fontSize: 14 }}>{headerName}</Typography>
    <Tooltip title="Auto-rule duplicates adsed based on tag">
      <InfoOutlined sx={{ fontSize: 18 }} />
    </Tooltip>
  </Box>
);

const LoadingOverlay = memo(() => (
  <Box
    sx={{
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      display: 'flex',
      alignContent: 'center',
      justifyContent: 'center',
      width: '100%',
      height: '100%',
      zIndex: 2,
      backgroundColor: 'rgba(255, 255, 255, 0.8)',
    }}
  >
    <CircularProgress title={'Ad Sets are loading...'} />
  </Box>
));
LoadingOverlay.displayName = 'LoadingOverlay';

const gridStyles = (t: Theme) => ({
  '.row-disabled': { color: t.palette.action.disabled },
  '.MuiDataGrid-row .MuiInputAdornment-positionEnd': {
    visibility: 'hidden',
  },
  '.MuiDataGrid-row:hover .MuiInputAdornment-positionEnd': {
    visibility: 'visible',
  },
});

const emptyTypographyStyles = (t: Theme) => ({
  color: t.palette.grey[400],
  fontSize: t.typography.h4.fontSize,
});

const CustomGridRow = (props: GridRowProps & RowPropsOverrides) => (
  <GridRow
    {...props}
    data-testid={props.row.id}
    data-budget={props.row.is_cbo ? 'cbo' : 'abo'}
  />
);

const AdSetsGrid = () => {
  const { watch, setValue, getValues, trigger } = useFormContext();
  const selectedAdSetsRows: AdSetEntry[] = watch('selectedAdSets');
  const marketingProcess: MarketingProcess = watch('marketingProcess');
  const selectedCampaigns: CampaignEntry[] = watch('selectedCampaigns');

  const [editingStartTimeId, setEditingStartTimeId] = useState<string | null>(null);

  const selectedAdSets = useMemo(
    () => selectedAdSetsRows?.map(({ id }) => id) ?? [],
    [selectedAdSetsRows],
  );

  const isSimplifiedFlow = useMemo(
    () => isSimplifiedFlowProcess(marketingProcess),
    [marketingProcess],
  );

  const handleOpenStartTimeDialog = useCallback((adSetId: string) => {
    setEditingStartTimeId(adSetId);
  }, []);

  const handleCloseStartTimeDialog = useCallback(() => {
    setEditingStartTimeId(null);
  }, []);

  const handleSetStartTime = useCallback((isoDateTime: string) => {
    if (!editingStartTimeId) return;
    setValue(`selectedAdSetsStartTimes.${editingStartTimeId}`, isoDateTime, { shouldValidate: true });
  }, [editingStartTimeId, setValue]);

  const handleRemoveStartTime = useCallback(() => {
    if (!editingStartTimeId) return;
    const allStartTimes: Record<string, string> = getValues('selectedAdSetsStartTimes');
    const rest = Object.fromEntries(
      Object.entries(allStartTimes).filter(([key]) => key !== editingStartTimeId),
    );
    setValue('selectedAdSetsStartTimes', rest, { shouldValidate: true });
  }, [editingStartTimeId, getValues, setValue]);

  const columns = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Adset Name',
        width: 570,
        disableColumnMenu: true,
      },
      {
        field: 'budget',
        headerName: 'ABO Budget',
        disableColumnMenu: true,
        width: 240,
        renderCell: (params: GridRenderCellParams<AdSetEntry>) => (
          <MemoizedAdSetBudgetField cellParams={params} />
        ),
        renderHeader: (params: GridColumnHeaderParams<AdSetEntry>) => {
          return (
            <BudgetsHeader
              tooltip="ABO budget is calculated for each AdSet"
              headerName={params.colDef.headerName}
            />
          );
        },
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 220,
        sortable: false,
        disableColumnMenu: true,
        renderHeader: (params: GridColumnHeaderParams<AdSetEntry>) => {
          if (!isSimplifiedFlow) {
            return <DuplicateStatusMenu formField={'selectedAdSets'} />;
          }
          return params.colDef.headerName;
        },
        renderCell: (params: GridRenderCellParams<AdSetEntry>) => {
          if (!isSimplifiedFlow) {
            return <AdSetStatusSwitcher cellParams={params} />;
          }
          return params.row.campaign_status ?? params.row.status;
        },
      },
      {
        field: 'start_time',
        headerName: 'Start time',
        disableColumnMenu: true,
        sortable: false,
        width: 180,
        renderHeader: (params: GridColumnHeaderParams<AdSetEntry>) => (
          <StartTimeHeader headerName={params.colDef.headerName} />
        ),
        renderCell: (params: GridRenderCellParams<AdSetEntry>) => (
          <MemoizedAdSetStartTimeField cellParams={params} onOpenDialog={handleOpenStartTimeDialog} />
        ),
      },
      {
        field: 'duplicates_count',
        headerName: 'Duplicates count',
        disableColumnMenu: true,
        sortable: false,
        width: 220,
        renderHeader: (params: GridColumnHeaderParams<AdSetEntry>) => (
          <DuplicatesHeader headerName={params.colDef.headerName} />
        ),
        renderCell: (params: GridRenderCellParams<AdSetEntry>) => (
          <MemoizedAdSetDuplicatesCountField cellParams={params} />
        ),
      },
      {
        field: 'tag',
        headerName: 'Tag',
        disableColumnMenu: true,
        sortable: false,
        width: 180,
        renderHeader: (params: GridColumnHeaderParams<AdSetEntry>) => (
          <TagHeader headerName={params.colDef.headerName} />
        ),
        renderCell: (params: GridRenderCellParams<AdSetEntry>) => (
          <AdSetTag cellParams={params} />
        ),
      },
    ],
    [isSimplifiedFlow, handleOpenStartTimeDialog],
  );

  const { rows, isPending, isError, error } = useRowsAdSets();

  const filteredRows = useMemo(() => {
    // If no rows are available, return previously selected ad sets
    if (!rows) {
      return selectedAdSetsRows ?? [];
    }

    const currentRowIds = new Set(rows.map(row => row.id));

    // Combine previously selected ad sets with current rows
    const previousSelected = selectedAdSetsRows?.filter(
      adSet => !currentRowIds.has(adSet.id),
    ) ?? [];

    const allRows = [...previousSelected, ...rows];

    // Return only ad sets that belong to selected campaigns
    return allRows.filter((adSet: AdSetEntry) =>
      selectedCampaigns.some((campaign: CampaignEntry) =>
        campaign.id === adSet.campaign_id,
      ),
    );
  }, [selectedAdSetsRows, rows, selectedCampaigns]);

  const handleSelectionMode = useCallback(
    (selectedRows: GridRowSelectionModel) => {
      if (!rows) return;

      const selectedRowIds = new Set(selectedRows);
      const currentRowIds = new Set(rows.map((row) => row.id));

      const currentSelected = rows
        .filter(({ id }) => selectedRowIds.has(id))
        .map((c) => ({
          ...c,
          status:
            selectedAdSetsRows?.find((s) => s.id === c.id)?.status ?? c.status,
        }));

      const previousSelected =
        selectedAdSetsRows?.filter(
          (adSet) =>
            !currentRowIds.has(adSet.id) && selectedRowIds.has(adSet.id),
        ) ?? [];

      const allSelectedAdSets = [...previousSelected, ...currentSelected];
      setValue('selectedAdSets', allSelectedAdSets);
    },
    [rows, selectedAdSetsRows, setValue],
  );

  const getRowId = useCallback((row: AdSetEntry) => row.id, []);

  const isRowSelectable = useCallback(
    (params: { row: AdSetEntry }) => !params.row.disabled,
    [],
  );

  const getRowClassName = useCallback(
    (params: { row: AdSetEntry }) =>
      params.row.disabled ? 'row-disabled' : '',
    [],
  );

  const initialState = useMemo(
    () => ({
      columns: {
        columnVisibilityModel: {
          start_time: !isSimplifiedFlow,
          duplicates_count: !isSimplifiedFlow,
          tag: !isSimplifiedFlow,
        },
      },
    }),
    [isSimplifiedFlow],
  );

  useEffect(() => {
    void trigger('selectedAdSets');
  }, [trigger, selectedAdSetsRows]);

  if (isPending && isEmpty(filteredRows)) {
    return <CircularProgress title={'Ad Sets are loading...'} />;
  }

  if (isError) {
    return (
      <SuspenseErrorFallback
        title={'Choose Ad Sets'}
        message={
          (error as { cause?: { error?: string } })?.cause?.error ??
          'An error occurred while fetching ad sets'
        }
      />
    );
  }

  if (isEmpty(filteredRows)) {
    return <Typography sx={emptyTypographyStyles}>No ad sets found</Typography>;
  }

  return (
    <Box style={{ position: 'relative', height: '100%', width: '100%' }}>
      {isPending && <LoadingOverlay />}
      <DataGrid
        rows={filteredRows as GridRowsProp<AdSetEntry>}
        columns={columns}
        slots={{
          row: CustomGridRow,
        }}
        checkboxSelection
        rowSelectionModel={selectedAdSets}
        onRowSelectionModelChange={handleSelectionMode}
        hideFooterPagination
        getRowId={getRowId}
        isRowSelectable={isRowSelectable}
        getRowClassName={getRowClassName}
        sx={gridStyles}
        // disableVirtualization={true}
        initialState={initialState}
      />
      <StartTimeDialog
        isOpen={!!editingStartTimeId}
        onClose={handleCloseStartTimeDialog}
        onSetTime={handleSetStartTime}
        onRemove={handleRemoveStartTime}
        currentValue={editingStartTimeId ? getValues(`selectedAdSetsStartTimes.${editingStartTimeId}`) as string | undefined : undefined}
      />
    </Box>
  );
};

const AdSetsForm = () => {
  return (
    <FormContainer
      title={'Choose Ad Sets'}
      sx={{ flexDirection: 'column', gap: 2 }}
    >
      <Box sx={{ display: 'flex', height: '100%', gap: 2, flexDirection: 'column', alignItems: 'start', justifyContent: 'start', width: '100%' }}>
      <Box sx={{
        display: 'flex',
        gap: 2,
        maxWidth: '100%',
        minWidth: '50%',
        flexDirection: 'row',
        alignItems: 'start',
        flexWrap: 'nowrap',
      }}>
        <ChipInput
          name="filterAdSetsByName"
          label="Search by name"
          inputLabel="Enter ad set name"
        />
      </Box>
      <Box sx={{flex: 1, height: '100%', width: '100%', position: 'relative' }}>
        <Box sx={{position: 'absolute', width: '100%', height: '100%', overflowY: 'auto'}}>
          <AdSetsGrid />
        </Box>
      </Box>
      </Box>
    </FormContainer>
  );
};

export default AdSetsForm;
